import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import Transition from '@/utils/Transition';
import { Input } from '@/components/Form/Input';

interface OptionType {
	value: any,
	content: React.ReactNode,
}

interface DropdownType {
	options: OptionType[],
	search?: boolean,
	className: string,
	selectSet?: any,
}

function DropdownSelect({ options, className, selectSet, search }: DropdownType) {
	const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
	const [selected, setSelected] = useState<OptionType>(options[0]);
	const [searchTerm, setSearchTerm] = useState<string>('');

	const trigger = useRef<HTMLButtonElement>(null);
	const dropdown = useRef<HTMLDivElement>(null);

	const userSelectionRef = useRef<boolean>(false);

	const filteredOptions = options.filter(option =>
		option.content && option.content.toString().toLowerCase().includes(searchTerm.toLowerCase())
	);

	const displayOptions = [selected, ...filteredOptions.filter(option => option.value !== selected.value)];

	// close on click outside
	useEffect(() => {
		const clickHandler = ({ target }: any) => {
			if ( !dropdown.current ) return;
			if ( !dropdownOpen || dropdown.current?.contains(target) || trigger.current?.contains(target) ) return;
			setDropdownOpen(false);
		};
		document.addEventListener('click', clickHandler);
		return () => document.removeEventListener('click', clickHandler);
	});

	// close if the esc key is pressed
	useEffect(() => {
		const keyHandler = ({ keyCode }: { keyCode: number }) => {
			if ( !dropdownOpen || keyCode !== 27 ) return;
			setDropdownOpen(false);
		};
		document.addEventListener('keydown', keyHandler);
		return () => document.removeEventListener('keydown', keyHandler);
	});

	useEffect(() => {
		if (userSelectionRef.current) {
			userSelectionRef.current = false;
			return;
		}

		const selectedOption = options.find((option: any) => option.selected);
		if (selectedOption) {
			setSelected(selectedOption);
		} else if (options.length > 0 && options[0].value !== selected.value) {
			setSelected(options[0]);
		}
	}, [options]);

	const handleSelect = (option: OptionType) => {
		userSelectionRef.current = true;
		selectSet && selectSet(option.value);
		setSelected(option);
		setDropdownOpen(false);
	};

	return (
		<div className="relative inline-flex w-full h-full">
			<button
				ref={trigger}
				type="button"
				className={classNames(
					className,
					'bg-white border border-[#E8E8E8] justify-between relative z-[2] text-brand-green py-2.5 md:py-3 px-3 md:px-4 rounded-[8px] text-left w-full h-10 md:h-12 focus:outline-0',
					displayOptions.length === 1 && 'cursor-default',
			  )}
				onClick={() => setDropdownOpen(!dropdownOpen)}
				aria-expanded={dropdownOpen}
			>
        <span className="flex items-center w-full">
          <span className="text-ellipsis whitespace-nowrap overflow-hidden">{selected.content}</span>
	        {displayOptions.length > 1 && (
	          <svg
		          className={classNames(
								'w-4 h-4 md:w-6 md:h-6 ml-auto',
			          dropdownOpen && 'rotate-180',
	            )}
		          width="24"
		          height="24"
		          viewBox="0 0 24 24"
		          fill="none"
		          xmlns="http://www.w3.org/2000/svg">
	              <path
		              d="M6 9L12 15L18 9"
		              stroke="#203B3F"
		              strokeWidth="2"
		              strokeMiterlimit="10"
		              strokeLinecap="round" />
	          </svg>
	        )}
        </span>
			</button>
			<Transition
				show={dropdownOpen}
				tag="div"
				className="absolute z-[1] top-0 left-0 w-full bg-white border border-[#E8E8E8] rounded-[8px] pt-[38px] md:pt-[46px]"
				enter="transition ease-out duration-100 transform"
				enterStart="opacity-0"
				enterEnd="opacity-100"
				leave="transition ease-out duration-100"
				leaveStart="opacity-100"
				leaveEnd="opacity-0"
			>
				<div
					ref={dropdown}
					className="scrollbox-sm max-h-[240px] overflow-auto"
					onFocus={() => setDropdownOpen(true)}
					onBlur={() => setDropdownOpen(false)}
				>
					{search && (
						<Input
							className="h-12"
							type="text"
							name="plan-price-amount"
							placeholder="Search"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							style={{ borderLeft: 0, borderRight: 0, borderTop: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
						/>
					)}
          {displayOptions
            .filter(option => option.value !== selected.value)
            .map((option: OptionType) => (
              <button
                key={option.value}
                className={classNames(
                  `flex items-center text-left w-full py-2.5 md:py-3 px-3 md:px-4 rounded-[8px] min-h-10 md:min-h-12 cursor-pointer`,
                  'border-b-transparent hover:bg-[#F7F6F2]'
                )}
                onClick={(e) => {
	                e.preventDefault();
	                handleSelect(option);
                }}
              >
                {option.content}
              </button>
            ))
					}
				</div>
			</Transition>
		</div>
	);
}

export default DropdownSelect;
